import { parseSessionState, type LiveSessionState } from "@/lib/live/session-state";
import { DEFAULT_SUBJECT, subjectIn } from "@/lib/live/video/camera-framing";
import type { VideoSubject } from "@/lib/live/video/video-types";

import type { ReplayStateLine } from "./replay-journal";

/**
 * Ce que filmait une caméra enregistrée : des mains, ou un visage.
 *
 * # Trois sources, dans cet ordre
 *
 * 1. Ce que l'interprète a corrigé après coup, que la fiche du replay rend
 *    (`cameras`) : la séance s'est trompée, il l'a dit, il a raison.
 * 2. Ce que la séance disait quand la caméra a cessé de filmer, que le journal
 *    des états a gardé : la table de cadrage, telle que le direct la lisait.
 * 3. Des mains : ce que filme une caméra dont personne n'a rien dit.
 *
 * Jamais le nom de la piste. Il a dit `hands` ou `face` un temps, et les
 * enregistrements de cette époque le portent encore ; c'était un second
 * endroit pour le même fait, qui disait « mains » de toutes les caméras faute
 * que rien ne lui demande autre chose.
 *
 * # Quand la caméra a cessé de filmer
 *
 * Et non quand elle a commencé : un rôle changé en plein récital est une
 * correction — « celle-là, c'est mon visage » —, et c'est le dernier mot qui
 * vaut pour tout le film. Un rôle qui changerait au fil de la lecture ferait
 * sauter l'image du pied du rouleau au coin, pour une erreur qu'on a réparée.
 *
 * Module pur : ni React, ni réseau.
 */
export function recordedSubject(
  camera: { readonly participant: string; readonly endMs: number },
  states: readonly ReplayStateLine[],
  corrected: Readonly<Record<string, VideoSubject>>,
): VideoSubject {
  const said = corrected[camera.participant];

  if (said !== undefined) {
    return said;
  }

  const state = stateAt(states, camera.endMs);

  return state === null ? DEFAULT_SUBJECT : subjectIn(state.cameras, camera.participant);
}

/**
 * Les lignes de mesure étaient-elles masquées ? Ce que la séance disait à la
 * fin de la prise : comme le rôle d'une caméra, c'est le dernier mot qui vaut
 * pour toute la prise, et la trame ne clignote pas en cours de lecture.
 */
export function recordedMeasuresHidden(states: readonly ReplayStateLine[], atMs: number): boolean {
  return stateAt(states, atMs)?.measuresHidden ?? false;
}

/**
 * Les corrections telles que la fiche du replay les rend, relues : une entrée
 * qui ne dit ni `hands` ni `face` est écartée, jamais la table entière.
 *
 * Une API d'avant la correction ne rend rien : rien n'est corrigé.
 */
export function readCorrectedSubjects(raw: unknown): Readonly<Record<string, VideoSubject>> {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return {};
  }

  const out: Record<string, VideoSubject> = {};

  for (const [participant, subject] of Object.entries(raw)) {
    if (participant !== "" && (subject === "hands" || subject === "face")) {
      out[participant] = subject;
    }
  }

  return out;
}

/**
 * L'état de séance en vigueur à cet instant : le dernier reçu avant lui.
 *
 * Les lignes sont rangées par arrivée (`loadJournals`). Une ligne illisible —
 * la dernière d'un journal tronqué — cède la place à celle d'avant. Avant la
 * première ligne, c'est la première qui vaut : l'enregistreur l'écrit en
 * entrant dans la salle, avant toute image.
 */
function stateAt(states: readonly ReplayStateLine[], atMs: number): LiveSessionState | null {
  let last = -1;

  for (let index = 0; index < states.length && states[index].receivedAtMs <= atMs; index += 1) {
    last = index;
  }

  for (let index = Math.max(last, 0); index >= 0 && index < states.length; index -= 1) {
    const state = parseSessionState(states[index].metadata);

    if (state !== null) {
      return state;
    }
  }

  return null;
}
